"use client";

import { createContext, useCallback, useContext, useState } from "react";

import { withBasePath } from "@/lib/base-path";

import type { ClientEventType } from "./bi.events";

interface VisitIdContextValue {
  visitId: string | null;
  setVisitId: (id: string) => void;
}

const VisitIdContext = createContext<VisitIdContextValue>({
  visitId: null,
  setVisitId: () => {},
});

export function VisitIdProvider({ children }: { children: React.ReactNode }) {
  const [visitId, setVisitId] = useState<string | null>(null);

  return (
    <VisitIdContext.Provider value={{ visitId, setVisitId }}>
      {children}
    </VisitIdContext.Provider>
  );
}

export function useVisitId(): string | null {
  return useContext(VisitIdContext).visitId;
}

export function useSetVisitId(): (id: string) => void {
  return useContext(VisitIdContext).setVisitId;
}

/**
 * Returns a fire-and-forget event sender that automatically includes the visitId
 * from the nearest VisitIdProvider. Must be used inside a VisitIdProvider.
 */
export function useTrackEvent() {
  const { visitId } = useContext(VisitIdContext);

  return useCallback(
    (eventType: ClientEventType, data: Record<string, unknown>) => {
      if (!visitId) {
        console.error(
          "[BI] useTrackEvent: no visitId in context — is VisitIdProvider mounted?",
        );
        return;
      }
      fetch(withBasePath("/api/bi/event"), {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ eventType, data: { visitId, ...data } }),
        keepalive: true,
      }).catch(() => {});
    },
    [visitId],
  );
}
